import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  fetchMe,
  logout as apiLogout,
  setUnauthorizedHandler,
  AUTH_BASE,
  MeResponse,
  SERVER_NOT_ANSWERING,
} from '../services/api';

export type AuthUser = MeResponse;

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  /** [DB-OUTAGE 2026-11-03] The first sign-in check got no real answer (server or database down) and keeps asking. */
  reconnecting: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Full URL that starts the Google sign-in flow. */
  loginUrl: string;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  reconnecting: false,
  logout: async () => {},
  refresh: async () => {},
  loginUrl: `${AUTH_BASE}/api/auth/google`,
});

export const useAuth = () => useContext(AuthContext);

/* [DB-OUTAGE 2026-11-03, user] The live database was killed and came back 6 s later (21:30 IST), and whoever opened the
   app in that moment got the LOGIN PAGE: any failed "who is signed in?" check (/auth/me) was read as "signed out". The user:
   "I want the proper fix for this so that the application will never get down". Now only the server's own answer 401
   means signed out. Anything else (the database or the server restarting, no internet, a 5xx, a time-out) is a passing
   gap: the signed-in person stays signed in, and the first check keeps asking (2 s, 4 s, 8 s, then every 15 s) behind a
   "not answering" screen until the server really answers. */
const isSignedOutAnswer = (e: any): boolean => e?.response?.status === 401;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
    } catch (e) {
      if (isSignedOutAnswer(e)) {
        setUser(null);
        return;
      }
      // [DB-OUTAGE 2026-11-03] Not a sign-out: whoever is signed in stays, and the caller (the login form) says why.
      throw new Error(SERVER_NOT_ANSWERING);
    }
  }, []);

  // Initial session probe on mount. [DB-OUTAGE 2026-11-03] Asks again until the server gives a real answer.
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const probe = async () => {
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
      try {
        const me = await fetchMe();
        if (!active) return;
        setUser(me);
      } catch (e) {
        if (!active) return;
        if (!isSignedOutAnswer(e)) {
          setReconnecting(true);
          tries += 1;
          timer = setTimeout(probe, Math.min(15000, 2000 * 2 ** (tries - 1)));
          return;
        }
        setUser(null);
      }
      setReconnecting(false);
      setLoading(false);
    };
    // Back on the internet while waiting: ask at once instead of at the next try.
    const onOnline = () => {
      if (timer) void probe();
    };
    window.addEventListener('online', onOnline);
    void probe();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  // Any 401 from a real API call (expired session) → bounce to login.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* ignore — clear locally regardless */
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, reconnecting, logout, refresh, loginUrl: `${AUTH_BASE}/api/auth/google` }}
    >
      {children}
    </AuthContext.Provider>
  );
};
